import { OrderedMap } from "immutable";
import { SecurityAppSecurityEvents } from "@graylog/enterprise-api";
import { qualifyUrl } from "util/URLUtils";
import { stringifyFilters, buildOpenSearchQuery } from "security-app/components/common/Filters/stringifyFilters";
import EnterpriseApiRoutes from "common/ApiRoutes";
import fetch from "logic/rest/FetchProvider";
import { attributes } from "security-app/components/SecurityEvents/Alerts/Constants";
import { defaultTimeRange } from "components/events/fetchEvents";
import parseSecurityEventFilters from "security-app/components/SecurityEvents/Alerts/parseSecurityEventFilters";
export const fetchSecurityEventByIds = async (eventIds) => fetch("POST", qualifyUrl(EnterpriseApiRoutes.SecurityEvents.detailsByIds().url), { event_ids: eventIds });
export const fetchSecurityEvents = async (page, perPage, query = null, orderBy = null, direction = null, filters = {}, userTimezone = null) => {
  const { alerts, timerange } = filters;
  const params = [
    `page=${page}`,
    `per_page=${perPage}`,
    `sort=${orderBy || "timestamp"}`,
    `direction=${direction || "desc"}`,
    `user_timezone=${encodeURI(userTimezone)}`,
    `alerts=${alerts || "include"}`,
    `timerange=${timerange || "0"}`
  ];
  if (query) params.push(`query=${encodeURI(query)}`);
  const mongoFiltersArr = [];
  const mongoFilters = Object.entries(filters).filter(
    ([key, values]) => key !== "alerts" && key !== "timerange" && values[0].backend !== "opensearch"
  ).reduce((cur, [key]) => Object.assign(cur, { [key]: filters[key] }), {});
  if (mongoFilters && Object.keys(mongoFilters).length > 0) {
    mongoFiltersArr.push(`${encodeURI(stringifyFilters(mongoFilters))}`);
  }
  if (mongoFiltersArr.length > 0) {
    const mongofilterStr = `filters=${mongoFiltersArr.join(";")}`;
    params.push(mongofilterStr);
  }
  const openSearchFilters = Object.entries(filters).filter(
    ([key, values]) => key !== "alerts" && key !== "timerange" && values[0].backend === "opensearch"
  ).reduce((cur, [key]) => Object.assign(cur, { [key]: filters[key] }), {});
  if (openSearchFilters && Object.keys(openSearchFilters).length > 0) {
    params.push(`openSearchFilters=${encodeURI(buildOpenSearchQuery(openSearchFilters))}`);
  }
  return fetch("GET", qualifyUrl(EnterpriseApiRoutes.SecurityEvents.index(params.join("&")).url));
};
export const fetchSecurityEventsFilterOptions = async ({
  timerange,
  fields
}) => {
  const payload = { timerange, fields };
  return fetch("POST", qualifyUrl(EnterpriseApiRoutes.SecurityEvents.filters().url), payload);
};
export const securityEventEntitiesKeyFn = (searchParams) => searchParams ? ["events", "search", searchParams] : ["events", "search"];
export const deserializeSecurityEventEntity = ({ event, ...rest }) => ({
  ...event,
  ...rest,
  id: rest.event_id
});
const deserializeSecurityEvents = ({
  security_events,
  page,
  per_page,
  grand_total,
  count
}) => ({
  attributes,
  list: security_events.map(deserializeSecurityEventEntity),
  pagination: { total: grand_total, page, per_page, count }
});
const mapSortBy = (attribute) => {
  if (!attribute) {
    return void 0;
  }
  switch (attribute) {
    case "scores":
      return "scores.normalized_risk";
    default:
      return attribute;
  }
};
export const parseSearchParams = (searchParams, timezone) => {
  var _a, _b, _c;
  const omFilters = OrderedMap.isOrderedMap(searchParams.filters) ? searchParams.filters : OrderedMap(Object.entries(searchParams.filters));
  const parsedFilters = parseSecurityEventFilters(omFilters, defaultTimeRange);
  const { timerange, filter } = parsedFilters;
  const sortBy = mapSortBy((_a = searchParams.sort) == null ? void 0 : _a.attributeId);
  return {
    page: searchParams.page,
    per_page: searchParams.pageSize,
    sort_by: sortBy,
    sort_direction: (_b = searchParams.sort) == null ? void 0 : _b.direction,
    query: (_c = searchParams.query) != null ? _c : "",
    filter,
    timerange,
    timezone
  };
};
export const fetchPaginatedSecurityEventEntities = (searchParams, timezone = null) => SecurityAppSecurityEvents.search(parseSearchParams(searchParams, timezone)).then(deserializeSecurityEvents);
export const newSecurityEvent = async ({
  data
}) => fetch("POST", qualifyUrl(EnterpriseApiRoutes.SecurityEvents.new().url), data);
export const updateSecurityEvent = async ({
  id,
  data
}) => fetch("PUT", qualifyUrl(EnterpriseApiRoutes.SecurityEvents.singleEvent(id).url), data);
export const fetchUsersNTeams = async () => {
  const { users } = await fetch("GET", qualifyUrl(EnterpriseApiRoutes.SecurityEvents.users().url));
  const { teams } = await fetch("GET", qualifyUrl(EnterpriseApiRoutes.SecurityEvents.teams().url));
  const normUsers = users.map((user) => ({ id: user.id, name: user.full_name, type: "User" }));
  const normTeams = teams.map((team) => ({ id: team.id, name: team.name, type: "Team" }));
  return Promise.resolve([...normUsers, ...normTeams]);
};
export const triggerSecurityEventNotification = async ({
  id,
  notificationId,
  stepId
}) => {
  const stepIdParam = stepId ? `?stepId=${stepId}` : "";
  await fetch(
    "POST",
    qualifyUrl(`${EnterpriseApiRoutes.SecurityEvents.notifications().url}/${id}/${notificationId}${stepIdParam}`)
  );
};
export const triggerBulkSecurityEventNotification = async ({
  ids,
  notificationId
}) => {
  await fetch("POST", qualifyUrl(`${EnterpriseApiRoutes.SecurityEvents.notifications().url}`), {
    event_ids: ids,
    notification_id: notificationId
  });
};
export const fetchEventDefinitions = async (page, perPage, query = null, orderBy = "title", direction = "asc") => {
  const params = [`page=${page}`, `per_page=${perPage}`, `sort=${orderBy}`, `order=${direction}`];
  if (query) params.push(`query=${encodeURI(query)}`);
  return fetch("GET", qualifyUrl(EnterpriseApiRoutes.SecurityEvents.definitions.index(params.join("&")).url));
};
export const fetchEventDefinitionDetailsByIds = async (eventDefinitionIds) => fetch("POST", qualifyUrl(EnterpriseApiRoutes.SecurityEvents.definitions.detailsByIds().url), {
  event_definition_ids: eventDefinitionIds
});
export const fetchEntityTypes = async () => fetch("GET", qualifyUrl(EnterpriseApiRoutes.SecurityEvents.definitions.types().url));
export const toggleEventDefinitionStatus = async ({ id, schedule }) => schedule ? fetch("PUT", qualifyUrl(EnterpriseApiRoutes.SecurityEvents.definitions.schedule(id).url)) : fetch("PUT", qualifyUrl(EnterpriseApiRoutes.SecurityEvents.definitions.unschedule(id).url));
export const copyEventDefinition = async ({ id }) => fetch("POST", qualifyUrl(EnterpriseApiRoutes.SecurityEvents.definitions.duplicate(id).url));
export const clearEventDefinitionNotificationsQueue = async ({ id }) => fetch("PUT", qualifyUrl(EnterpriseApiRoutes.SecurityEvents.definitions.clearQueue(id).url));
export const deleteEventDefinition = async ({ id }) => fetch("DELETE", qualifyUrl(EnterpriseApiRoutes.SecurityEvents.definitions.details(id).url));
export const blkEnableDefinition = async ({ entity_ids }) => fetch("POST", qualifyUrl(EnterpriseApiRoutes.SecurityEvents.definitions.bulkSchedule().url), { entity_ids });
export const blkDisableDefinition = async ({ entity_ids }) => fetch("POST", qualifyUrl(EnterpriseApiRoutes.SecurityEvents.definitions.bulkUnschedule().url), { entity_ids });
export const blkDeleteDefinition = async ({ entity_ids }) => fetch("POST", qualifyUrl(EnterpriseApiRoutes.SecurityEvents.definitions.bulkDelete().url), { entity_ids });
export const fetchNotifications = async (page, perPage, query = null, orderBy = "title", direction = "asc") => {
  const params = [`page=${page}`, `per_page=${perPage}`, `sort=${orderBy}`, `order=${direction}`];
  if (query) params.push(`query=${encodeURI(query)}`);
  return fetch("GET", qualifyUrl(EnterpriseApiRoutes.SecurityEvents.securityNotifications.index(params.join("&")).url));
};
export const fetchNotificationDetails = async (notificationId) => fetch("GET", qualifyUrl(EnterpriseApiRoutes.SecurityEvents.securityNotifications.details(notificationId).url));
export const deleteNotification = async ({ id }) => fetch("DELETE", qualifyUrl(EnterpriseApiRoutes.SecurityEvents.securityNotifications.details(id).url));
export const updateNotification = async ({ id, payload }) => fetch("PUT", qualifyUrl(EnterpriseApiRoutes.SecurityEvents.securityNotifications.details(id).url), payload);
export const testNotification = async ({ id }) => fetch("POST", qualifyUrl(EnterpriseApiRoutes.SecurityEvents.securityNotifications.test(id).url));
export const fetchDetectionChainEventDefinitionSummary = async (eventId) => fetch("GET", qualifyUrl(EnterpriseApiRoutes.SecurityEvents.definitions.ThreatCampaigns.summary(eventId).url));
export const fetchDetectionChainEvents = async (detectionChainId) => fetch("GET", qualifyUrl(EnterpriseApiRoutes.SecurityEvents.definitions.ThreatCampaigns.events(detectionChainId).url));
